"use client";

import { Fragment, useState } from "react";
import styles from "./styles.module.scss";
import cn from "clsx";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { removeLastElement } from "./utils";
import Image from "next/image";
import ArrowUp from "~/icons/arrow-up.svg";
import ArrowDown from "~/icons/arrow-down.svg";
import TailArrow from "@/SvgIcons/TailArrow";

type FooterSubItem = {
  text: string;
  url: string;
  target?: string;
};

type FooterLinkItem = {
  enabled: boolean;
  Item: FooterSubItem;
  SubItem?: FooterSubItem[];
};

type FooterLinksType = {
  footerLinksOne: FooterLinkItem[];
  footerLinksTwo: FooterLinkItem[];
  footerLinksThree: FooterLinkItem[];
  footerLinksFour: FooterLinkItem[];
};

export default function FooterLinks({
  footerLinks,
}: {
  footerLinks: FooterLinksType;
}) {
  const [showList, setShowList] = useState(false);

  const toggleList = () => {
    setShowList(!showList);

    pushToDataLayer({
      event: "generic_cta_click",
      cta_text: "Show More",
      section_name: "footer",
      page_type: getGlobalStateValue('pageType'),
    });
  };

  const handleGaClick = (itemData: { ctaText?: string; sectionText?: string }) => {
    const {ctaText="", sectionText="" } = itemData || {};
    pushToDataLayer({
      event: "footer_interaction",
      cta_text: ctaText || "NA",
      section_name: sectionText || "NA",
      page_type : getGlobalStateValue('pageType')
    });
  };

  const firingWebEngage=(source:string,source2:string)=>{
    window?.webengage?.track("Footer Clicked", {
      "Footer Name": source,
      "Sub Category": source2,
    })
  }

  return (
    <>
      <div
        className={cn(
          "w-full flex justify-center items-center",
          styles.readmoreCTA
        )}
      >
        <button
          id = "footer-showbtn"
          onClick = {toggleList}
          className={styles.showMoreButton}
          aria-label={showList ? "show less" : "show more"}
        >
          {showList ? "show less" : "show more"}
          <TailArrow className={styles.arrowDownIcon} rotate={showList? -180 :0}/>
        </button>
      </div>
      <div
        className={cn(styles.hiddenFooterLinks, {
          [styles.showFooterLinks]: showList,
        })}
      >
        <div className={styles.footerLinksContent}>
          <div
            className={cn(
              "flex flex-col my-8 1025:grid 1025:grid-cols-4 1025:grid-rows-1 lg:grid lg:grid-rows-1 lg:grid-cols-4 1025:gap-x-4",
              styles.hiddenMenuContainer
            )}
          >
            <ul className={cn(styles.menu1, "col-span-1")}>
            {footerLinks.footerLinksOne
              ?.filter((x) => x.enabled)
              ?.map((link, index) => (
                <Fragment key={index}>
                  <li
                    className={cn(
                      styles.headingLink,
                      index > 0 &&
                      link?.SubItem?.length &&
                      styles.headingLinkWithChild, "w-fit"
                    )}
                  >
                    <a href={link.Item.url?.startsWith("/") ? removeLastElement(process.env.NEXT_PUBLIC_HOST_URL) + link.Item.url : link.Item.url}
                      onClick={(e) => {
                        handleGaClick({
                          sectionText: link?.Item?.text,
                          ctaText: link?.Item?.text || "NA"
                        });
                        firingWebEngage(link?.Item?.text, "General");
                      }
                      }
                      target={link?.Item?.target || "_self"}
                    >
                      {link.Item.text}
                    </a>
                  </li>
                  {link?.SubItem?.map((subLink: any, index: number) => (
                    <li className={cn(styles.subMenuItem, 'w-fit')} key={index}>
                      <a
                        href={subLink?.url}
                        onClick={(e) => {
                          handleGaClick({
                            sectionText: link?.Item?.text,
                            ctaText: subLink?.text || "NA"
                          });
                          firingWebEngage(subLink?.text,"General");
                        }} 
                        target={subLink?.target || "_self"}>
                        {subLink?.text}
                      </a>
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>
            <ul className={cn(styles.menu2, "col-span-1 w-fit")}>
            {footerLinks.footerLinksTwo
              ?.filter((x) => x.enabled)
              ?.map((link, index) => (
                <Fragment key={index}>
                  <li
                    className={cn(
                      styles.headingLink,
                      index > 0 &&
                      link?.SubItem?.length &&
                      styles.headingLinkWithChild
                    )}
                  >
                    {link.Item.text}
                  </li>
                  {link?.SubItem?.map((subLink, index) => (
                    <li className={cn(styles.subMenuItem, "w-fit")} key={index}>
                      <a href={subLink?.url} target={subLink?.target || "_self"}
                        onClick={(e) => {
                          handleGaClick({
                            sectionText: link?.Item?.text || "NA",
                            ctaText: subLink?.text || "NA"
                          })
                          firingWebEngage(subLink?.text, link?.Item?.text);
                        }
                        }
                      >
                        {subLink?.text}
                      </a>
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>

            <ul className={cn(styles.menu3, "col-span-1 w-fit")}>
            {footerLinks.footerLinksThree
              ?.filter((x) => x.enabled)
              ?.map((link, index) => (
                <Fragment key={index}>
                  <li
                    className={cn(
                      styles.headingLink,
                      index > 0 &&
                      link?.SubItem?.length &&
                      styles.headingLinkWithChild
                    )}
                    key={index}
                  >
                    {link.Item.text}
                  </li>
                  {link?.SubItem?.map((subLink, index) => (
                    <li className={cn(styles.subMenuItem, "w-fit")} key={index}>
                      <a href={subLink?.url} target={subLink?.target || "_self"}
                        onClick={(e)=>{
                          handleGaClick({
                            sectionText : link?.Item?.text || "NA",
                            ctaText : subLink?.text || "NA"
                          })
                          firingWebEngage(subLink?.text,link?.Item?.text);
                        }
                        }
                      >
                        {subLink?.text}
                      </a>
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>
            <ul className={cn(styles.menu4,"col-span-1 w-fit")}>
            {footerLinks.footerLinksFour
              ?.filter((x) => x.enabled)
              ?.map((link, index) => (
                <Fragment key={index}>
                  <li
                    className={cn(
                      styles.headingLink,
                      index > 0 &&
                        link?.SubItem?.length &&
                        styles.headingLinkWithChild
                    )}
                    key={index}  
                  >
                    {link.Item.text}
                  </li>
                  {link?.SubItem?.map((subLink, index) => (
                    <li className={cn(styles.subMenuItem, "w-fit")} key={index}>
                      <a href={subLink?.url} target={subLink?.target || "_self"}
                        onClick={(e) => {
                          handleGaClick({
                            sectionText: link?.Item?.text || "NA",
                            ctaText: subLink?.text || "NA"
                          })
                          firingWebEngage(subLink?.text, link?.Item?.text);
                        }
                        }
                      >
                        {subLink?.text}
                      </a>
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>
          </div>

          <hr className="text-[#D4D4D4] stroke-[1.5px]" />
        </div>
      </div>
    </>
  );
}
